import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import {
  AuthContext,
  Organization,
  setAnalyticsContext,
  track,
  eventTypes,
  SELECTED_ORG_COOKIE,
  readSelectedOrganizationId as readOrgCookie,
} from 'torqcloud-ui'
import { awsConfig } from '../config'

const ORG_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7

// Only send a domain when one is configured. Cognito always sets it, but the
// self-hosted (Keycloak) build leaves it undefined, and `domain=undefined` is
// rejected by the browser, which would stop the cookie being stored at all.
function orgCookieAttributes() {
  const domain = awsConfig.cookieDomain ? `domain=${awsConfig.cookieDomain}; ` : ''
  return `${domain}path=/; SameSite=Lax`
}

function writeOrgCookie(id: string) {
  // `Max-Age` is the real cookie attribute; a camelCased `maxAge` is ignored by
  // the browser, which downgrades this to a session cookie and loses the
  // selected organization as soon as the browser is closed.
  document.cookie = `${SELECTED_ORG_COOKIE}=${id}; ${orgCookieAttributes()}; Max-Age=${ORG_COOKIE_MAX_AGE_SECONDS}`
}

function clearOrgCookie() {
  document.cookie = `${SELECTED_ORG_COOKIE}=; ${orgCookieAttributes()}; Max-Age=0`
}

export type OrganizationContextType = {
  currentOrganizationId?: string
  organizations: Organization[]
  setCurrentOrganizationId: (id: string) => void
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

function resolveOrgId(orgs: Organization[], currentOrganizationId?: string): string | undefined {
  if (!orgs.length) return undefined

  const isCurrentValid = currentOrganizationId && orgs.some(o => o.organization_id === currentOrganizationId)
  if (isCurrentValid) {
    return currentOrganizationId
  }

  const cookieOrgId = readOrgCookie()
  const isValid = cookieOrgId && orgs.some(o => o.organization_id === cookieOrgId)
  return isValid ? cookieOrgId : orgs.at(0)?.organization_id
}

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const authContext = useContext(AuthContext)
  const authOrganizations = authContext.organizations || []
  const hasMetadata = authContext.hasMetadata
  const [organizations, setOrganizations] = useState<Organization[]>(authOrganizations)
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | undefined>(() => resolveOrgId(authOrganizations))

  useEffect(() => {
    const updatedOrgs = authContext.organizations || []
    const resolvedOrganizationId = resolveOrgId(updatedOrgs, currentOrganizationId)

    setOrganizations(updatedOrgs)
    setCurrentOrganizationId(resolvedOrganizationId)

    if (resolvedOrganizationId) {
      writeOrgCookie(resolvedOrganizationId)
    } else if (hasMetadata) {
      // Only forget the selection once /me has confirmed the user really has no
      // organizations. This effect also runs on every page load before metadata
      // arrives, when `organizations` is still empty; clearing the cookie there
      // would delete the selection the reload needs to restore, silently
      // dropping the user back into their first (default) organization.
      clearOrgCookie()
    }
  }, [authContext.organizations, currentOrganizationId, hasMetadata])

  // Keep the analytics shared-property context current. The analytics wrapper
  // cannot read React context, so we push organization, user, and plan into its
  // module-level ref whenever any of them changes.
  const plan = authContext.user.account.subscription.plan.name
  const userId = authContext.user.id
  const currentOrganization = organizations.find(organization => organization.organization_id === currentOrganizationId)
  const organizationType = currentOrganization?.organization_level
  const organizationRole = currentOrganization?.organization_role
  useEffect(() => {
    setAnalyticsContext({
      organizationId: currentOrganizationId,
      organizationType,
      organizationRole,
      userId,
      plan,
    })
  }, [currentOrganizationId, organizationType, organizationRole, userId, plan])

  const handleSetOrganizationId = (id: string) => {
    if (!organizations.some(organization => organization.organization_id === id)) {
      return
    }
    if (id !== currentOrganizationId) {
      track(eventTypes.ORGANIZATION_SWITCHED, {
        fromOrganizationId: currentOrganizationId,
        toOrganizationId: id,
      })
    }
    setCurrentOrganizationId(id)
    writeOrgCookie(id)
  }

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganizationId,
        organizations,
        setCurrentOrganizationId: handleSetOrganizationId,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }
  return context
}
